import { useCallback, useEffect, useState } from 'react'
import {
  listInvites, createInvite, revokeInvite,
  type Invite,
} from '../../api'
import { dateTime, relativeDays } from '../../format'

/**
 * The allowlist, which is what an invitation actually is. It lives as a tab of
 * Members: inviting someone and seeing who is already in are one job.
 *
 * Proving an address admits nobody on its own: a code proves only that
 * somebody reads the mailbox, any Google account on earth completes Google's
 * consent, and a company's identity provider vouches for its own staff, not for
 * who may use docsdone. What decides is a row here naming that exact address.
 * An uninvited account is turned away with nothing written.
 *
 * The page says so without naming a way in. It used to say "sign in with
 * Google", in an environment where Google sign-in is off and people sign in
 * with a code or through their company.
 *
 * Accepted and revoked invitations stay listed. "Who did we invite" is a
 * question about the past as much as the present, and an accepted invitation
 * is the record of how somebody came to be a member.
 */

/**
 * An invitation is open only while nothing has closed it and it has not run
 * out. Both `accepted_at` and `revoked_at` are terminal, and they are checked
 * before the clock: an invitation accepted last year is "accepted", not
 * "expired".
 */
function statusOf(i: Invite): string {
  if (i.revoked_at) return 'withdrawn'
  if (i.accepted_at) return 'accepted'
  if (new Date(i.expires_at).getTime() < Date.now()) return 'expired'
  return 'open'
}

export function InviteList({ orgId }: { orgId: string }) {
  const [invites, setInvites] = useState<Invite[] | null>(null)
  const [error, setError] = useState('')
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<'admin' | 'member'>('member')
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState('')

  const load = useCallback(() => {
    listInvites(orgId)
      .then((r) => setInvites(r.invites ?? []))
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not read the invitations.'))
  }, [orgId])

  // Only the load. Resetting to "loading" on an organization change is done by
  // remounting — the caller keys this component on the organization id — which
  // is what keeps one organization's roster from flashing under another's
  // heading without a cascading render to undo it.
  useEffect(load, [load])

  async function invite(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError('')
    setNotice('')
    try {
      await createInvite(orgId, { email: email.trim(), role })
      // There is no invitation link to send: whoever signs in with the address
      // is let in, by any way in this docsdone offers.
      setNotice(`${email.trim()} can now sign in. Nothing has been emailed. Tell them to sign in with that address.`)
      setEmail('')
      load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create that invitation.')
    } finally {
      setBusy(false)
    }
  }

  async function withdraw(i: Invite) {
    setError('')
    setNotice('')
    try {
      await revokeInvite(orgId, i.id)
      setNotice(`The invitation to ${i.email} has been withdrawn.`)
      load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not withdraw that invitation.')
    }
  }

  return (
    <>
      <div className="card" style={{ marginBottom: '1rem' }}>
        <form onSubmit={(e) => void invite(e)}>
          <div className="row" style={{ alignItems: 'flex-end', gap: '.75rem' }}>
            <div style={{ flex: 1, minWidth: '14rem' }}>
              <label htmlFor="invite-email">Invite an address</label>
              <input
                id="invite-email"
                type="email"
                required
                placeholder="name@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="invite-role">As</label>
              <select
                id="invite-role"
                value={role}
                onChange={(e) => setRole(e.target.value as 'admin' | 'member')}
              >
                <option value="member">Member</option>
                <option value="admin">Administrator</option>
              </select>
            </div>
            <button className="btn" disabled={busy || email.trim() === ''}>
              {busy ? 'Inviting…' : 'Invite'}
            </button>
          </div>
        </form>
        <p className="small faint" style={{ marginTop: '.6rem', marginBottom: 0 }}>
          Admits exactly this address, whichever way they sign in.
        </p>
      </div>

      {error && <div className="notice notice-bad" style={{ marginBottom: '.9rem' }}>{error}</div>}
      {notice && <div className="notice notice-good" style={{ marginBottom: '.9rem' }}>{notice}</div>}

      {invites === null && !error && <p className="muted">Loading…</p>}

      {invites?.length === 0 && (
        <div className="card empty">
          <h2>Nobody invited yet</h2>
          <p className="muted">Invite an address to let that person join this organization.</p>
        </div>
      )}

      {!!invites?.length && (
        <div className="card card-tight table-wrap">
          <table className="table table-rows">
            <thead>
              <tr>
                <th>Address</th>
                <th>Role</th>
                <th>Status</th>
                <th>Invited</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {invites.map((i) => {
                const status = statusOf(i)
                const open = status === 'open'
                return (
                  <tr key={i.id}>
                    <td><strong>{i.email}</strong></td>
                    <td className="muted small">{i.role === 'admin' ? 'Administrator' : 'Member'}</td>
                    <td>
                      <span className="tag">{status}</span>
                      {open && (
                        <div className="faint small">expires {relativeDays(i.expires_at)}</div>
                      )}
                      {i.accepted_at && (
                        <div className="faint small">{dateTime(i.accepted_at)}</div>
                      )}
                    </td>
                    <td className="muted small">{dateTime(i.created_at)}</td>
                    <td style={{ textAlign: 'right' }}>
                      {open && (
                        <button
                          className="btn btn-secondary btn-small"
                          aria-label={`Withdraw the invitation to ${i.email}`}
                          onClick={() => void withdraw(i)}
                        >
                          Withdraw
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}
